import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db94rq8kk00l6eltrp20";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setErrorMessage("");
      form.reset();
      setCount(0);
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const success = response.ok && parsed?.code === "OK";

      if (success) {
        setStatus("success");
        setErrorMessage("");
        form.reset();
        setCount(0);
      } else {
        setStatus("error");
        setErrorMessage(serverMessage || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setErrorMessage("网络异常，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="about-contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你的称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="请输入称呼"
            className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱地址
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="请输入邮箱"
            className="h-11 w-full rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          onChange={(e) => setCount(e.target.value.length)}
          placeholder="请填写你的建议或问题（500字以内）"
          className="w-full rounded-md border border-background-300 bg-background-100 p-3 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-500">{count} / 500</p>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="aux-field-ext"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-8"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交留言
          </>
        )}
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900">
          <i className="ri-checkbox-circle-line text-base"></i>
          留言已提交成功，感谢你的反馈，我们会尽快处理。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm font-medium text-secondary-900">
          <i className="ri-error-warning-line text-base"></i>
          {errorMessage}
        </p>
      )}
    </form>
  );
}